import { config } from "./config.js";
import type { HostnameRecord } from "@agencylens/shared";

export interface HostnamesListResponse {
  hostnames: HostnameRecord[];
  limit: number | "unlimited";
  cnameTarget: string;
}

export interface HostnameCreateResponse {
  hostname: HostnameRecord;
  cnameTarget: string;
  instructions: string;
}

async function request<T>(
  path: string,
  jwt: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${config.hostnamesWorkerBase}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`Hostnames worker non-JSON ${res.status}: ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    const err = data as { error?: string; hint?: string };
    const e = new Error(err.error ?? `HTTP ${res.status}`);
    Object.assign(e, err);
    (e as Error & { status?: number }).status = res.status;
    throw e;
  }
  return data as T;
}

export async function listHostnames(jwt: string): Promise<HostnamesListResponse> {
  return request<HostnamesListResponse>("/hostnames", jwt, { method: "GET" });
}

export async function createHostname(
  jwt: string,
  hostname: string,
): Promise<HostnameCreateResponse> {
  return request<HostnameCreateResponse>("/hostnames", jwt, {
    method: "POST",
    body: JSON.stringify({ hostname }),
  });
}

export async function deleteHostname(jwt: string, id: string): Promise<void> {
  await request<{ ok: true }>(`/hostnames/${id}`, jwt, { method: "DELETE" });
}

export async function refreshHostnameStatus(
  jwt: string,
  id: string,
): Promise<{ hostname: HostnameRecord }> {
  return request<{ hostname: HostnameRecord }>(
    `/hostnames/${id}/status`,
    jwt,
    { method: "GET" },
  );
}
